import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LikesModal from './LikesModal';
import { getPostLikeUsers } from '@/lib/supabase/social';
import AvatarSkeleton from '@/components/ui/avatar/AvatarSkeleton';

interface PostActionsProps {
  id: string;
  isLiked: boolean;
  likesCount: number;
  commentsCount: number;
  sharesCount: number;
  showComments: boolean;
  onLike: () => void;
  onComment: () => void;
  shareButton: React.ReactNode;
}

const PostActions: React.FC<PostActionsProps> = ({
  id,
  isLiked,
  likesCount,
  commentsCount,
  sharesCount,
  showComments,
  onLike,
  onComment,
  shareButton
}) => {
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likeUsers, setLikeUsers] = useState<
    Array<{ auth_id: string; full_name: string; avatar_url: string }>
  >([]);
  const [isLoadingLikes, setIsLoadingLikes] = useState(false);

  useEffect(() => {
    if (likesCount > 0) {
      const fetchLikeUsers = async () => {
        setIsLoadingLikes(true);
        try {
          const users = await getPostLikeUsers(id);
          setLikeUsers(users.slice(0, 3));
        } catch (error) {
          console.error('Error fetching like users:', error);
        } finally {
          setIsLoadingLikes(false);
        }
      };
      fetchLikeUsers();
    } else {
      setLikeUsers([]);
    }
  }, [id, likesCount]);

  return (
    <div
      data-post-actions
      className="flex items-center justify-between mt-6 pt-4 border-t border-dark-300"
    >
      {/* LIKE */}
      <motion.button
        onClick={(e) => {
          e.stopPropagation();      // keep click inside PostActions
          onLike();
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="relative flex items-center space-x-2 group overflow-visible"
      >
        <AnimatePresence mode="wait">
          {isLiked ? (
            <motion.div
              key="liked"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="relative overflow-visible"
            >
              <Heart className="h-6 w-6 text-red-500 fill-red-500" />
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{
                    scale: 2,
                    opacity: 0,
                    x: Math.cos((i * Math.PI) / 4) * 10,
                    y: Math.sin((i * Math.PI) / 4) * 10
                  }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div
                    className="w-1 h-1 rounded-full bg-red-500"
                    style={{ transform: `rotate(${i * 45}deg) translateY(-10px)` }}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="unliked"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <Heart className="h-6 w-6 text-black dark:text-white group-hover:text-red-500/50 transition-colors" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* like-count & avatars */}
        <div className="flex items-center space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (likesCount > 0) setShowLikesModal(true);
            }}
            className={`text-sm font-medium transition-colors ${
              isLiked ? 'text-red-500' : 'text-black dark:text-white'
            } ${likesCount > 0 ? 'hover:underline' : ''}`}
            disabled={likesCount === 0}
          >
            {likesCount}
          </button>

          {likesCount > 0 && (
            <div
              className="flex items-center ml-0.5 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowLikesModal(true);
              }}
            >
              {isLoadingLikes ? (
                <AvatarSkeleton size={20} count={Math.min(3, likesCount)} />
              ) : (
                <>
                  <div className="flex -space-x-1.5 overflow-hidden">
                    {likeUsers.map((user) => (
                      <img
                        key={user.auth_id}
                        src={
                          user.avatar_url ||
                          'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
                        }
                        alt={user.full_name}
                        className="inline-block h-5 w-5 rounded-full object-cover dark:border-dark-300/30 light:border-white/50 border shadow-sm"
                        title={user.full_name}
                      />
                    ))}
                  </div>
                  {likesCount > likeUsers.length && (
                    <span className="text-xs text-black dark:text-white ml-1">
                      +{likesCount - likeUsers.length}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </motion.button>

      {/* COMMENT */}
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          onComment();
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="flex items-center space-x-2 text-black dark:text-white hover:text-gold-400 transition-colors"
      >
        <MessageCircle className={`h-6 w-6 ${showComments ? 'text-gold-400' : ''}`} />
        <span className="text-sm">{commentsCount}</span>
      </motion.button>

      {/* SHARE */}
      <div onClick={(e) => e.stopPropagation()}>{shareButton}</div>

      {/* Likes modal */}
      <LikesModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        postId={id}
        title={`${likesCount} ${likesCount === 1 ? 'Like' : 'Likes'}`}
      />
    </div>
  );
};

export default PostActions;
